import type { CSSProperties, ReactNode } from 'react'
import { cn } from '@/lib/cn'

export type PageHeroTone = 'bid' | 'ask' | 'info'

/** Resolved color tokens for hero glow (matches Deposit/Settings intensity). */
const TONE_VARS: Record<PageHeroTone, string> = {
  bid: 'var(--color-bid)',
  ask: 'var(--color-danger)',
  info: 'var(--color-info)',
}

export interface PageHeroProps {
  eyebrow: string
  title: string
  subtitle?: string
  tone?: PageHeroTone
  /** Gradient origin — deposit left, withdraw right */
  glow?: 'start' | 'end' | 'center'
  children?: ReactNode
  className?: string
}

/**
 * Shared page header — same radial glow pattern as Settings / Deposit.
 * Deposit → bid (green), Withdraw → ask/danger (red), others → info (blue).
 */
export function PageHero({
  eyebrow,
  title,
  subtitle,
  tone = 'info',
  glow = 'start',
  children,
  className,
}: PageHeroProps) {
  const color = TONE_VARS[tone]
  const at =
    glow === 'end' ? '90% 0%' : glow === 'center' ? '50% 0%' : '0% 0%'
  const at2 =
    glow === 'end' ? '60% 100%' : glow === 'center' ? '80% 120%' : '70% 100%'

  return (
    <div
      className={cn(
        'relative shrink-0 overflow-hidden border-b border-terminal-border/40',
        className,
      )}
      style={{ '--hero': color } as CSSProperties}
    >
      {/* Primary glow — mirrors SettingsPage / Deposit intensity */}
      <div
        className="pointer-events-none absolute inset-0 opacity-90"
        style={{
          background: `radial-gradient(ellipse 80% 70% at ${at}, color-mix(in oklab, var(--hero) 22%, transparent), transparent 55%)`,
        }}
      />
      {/* Secondary wash so Withdraw / News / Analysis read as clearly as Deposit */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 55% 50% at ${at2}, color-mix(in oklab, var(--hero) 10%, transparent), transparent 60%)`,
        }}
      />
      <div className="relative px-4 py-5 sm:px-6">
        <p
          className="text-[10px] font-bold uppercase tracking-[0.16em]"
          style={{ color: 'var(--hero)' }}
        >
          {eyebrow}
        </p>
        <h1 className="mt-1 text-xl font-semibold tracking-tight text-terminal-fg sm:text-2xl">
          {title}
        </h1>
        {subtitle ? (
          <p className="mt-1.5 max-w-xl text-xs leading-relaxed text-terminal-muted sm:text-sm">
            {subtitle}
          </p>
        ) : null}
        {children ? <div className="mt-4">{children}</div> : null}
      </div>
    </div>
  )
}

export function pageHeroTextClass(tone: PageHeroTone): string {
  if (tone === 'bid') return 'text-bid'
  if (tone === 'ask') return 'text-danger'
  return 'text-info'
}

export function pageHeroBgClass(tone: PageHeroTone): string {
  if (tone === 'bid') return 'bg-bid'
  if (tone === 'ask') return 'bg-danger'
  return 'bg-info'
}

export function pageHeroSoftClass(tone: PageHeroTone): string {
  if (tone === 'bid') return 'bg-bid-badge text-bid'
  if (tone === 'ask') return 'bg-danger-badge text-danger'
  return 'bg-info-badge text-info'
}
